import { React, useEffect, useState } from "react";
import {
    Autocomplete,
    Button,
    TextField,
    IconButton,
    Tooltip,
    Avatar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";
import { verifyDomain, getBusinessType } from "../../Actions/OrganizationAction";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    stepperTextFieldSx,
    stepperTextareaSx,
    stepperAutocompleteInputSx,
} from "../Tenant/stepperFormStyles";
import "./OrganizationInfoStep.css";

const OrganizationInfoStep = ({
    formik,
    qbotDomain,
    countryData,
    stateData,
    getStateData,
    dateFormatOptions,
    handleDateFormatChange,
    organizationDataById = null,
    timeFormatOptions,
    handleTimeFormatChange,
    handleTimeZoneChange,
    timeZone,
    businessType,
    getBusinessStateDate,
    businessStateData,
    isPaycentralEdit,
    setIsPaycentralEdit,
    isTrialPortalCreation
}) => {
    const [isVerified, setIsVerified] = useState(false);
    const [hasCustomDomain, setHasCustomDomain] = useState(false);
    const [isImageEdited, setIsImageEdited] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [buttonColor, setButtonColor] = useState("");
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const isEditMode = organizationDataById;

    const handleFileChange = (event) => {
        setIsImageEdited(true);
        formik.setFieldValue(
            "organizationLogo",
            event.currentTarget.files[0]
        );

        if (event.currentTarget.files && event.currentTarget.files[0]) {
            const reader = new FileReader();
            reader.onload = (e) => {
                formik.setFieldValue(
                    "imagePreview",
                    e.target.result
                );
            };
            reader.readAsDataURL(event.currentTarget.files[0]);
            setIsPaycentralEdit(true);
        }
    };

    const extractSubdomain = (domainUrl) => {
        const baseDomain = "netcluesdemo.com";
        if (!domainUrl) return "";
        return domainUrl
            .replace(/^https?:\/\//i, "") // Remove protocol
            .replace(`.${baseDomain}`, "") // Remove base domain
            .trim();
    };

    const handleEditClick = () => {
        setButtonColor("error");
        setIsVerified(false);
        setIsEditing(true);
        setHasCustomDomain(false);
        const currentDomain = formik.values.domainUrl;
        const subdomain = extractSubdomain(
            currentDomain
        );
        formik.setFieldValue("domainUrl", subdomain);

        formik.setFieldValue("isVerified", false);
        setIsButtonDisabled(true);
    };

    const generateDefaultDomainUrl = (subdomain) => {
        const baseDomain = "netcluesdemo.com";
        if (!subdomain) return "";
        const cleanSubdomain = subdomain
            .replace(/\s+/g, "") // Remove spaces
            .replace(/[^a-zA-Z0-9]/g, "") // Remove special characters
            .toLowerCase(); // Convert to lowercase
        return `https://${cleanSubdomain}.${baseDomain}`;
    };

    const handleSaveDomainUrl = () => {
        const subdomain = formik.values.domainUrl.trim();
        const defaultDomainUrl = generateDefaultDomainUrl(
            formik.values.userName
        );

        // Generate the full domain URL from the edited subdomain
        const updatedDomainUrl = generateDefaultDomainUrl(subdomain);

        // Validate the domain URL format
        const domainUrlRegex =
            /^(https?:\/\/)?([a-zA-Z0-9._-]+(\.[a-zA-Z]{2,}))$/;
        if (!domainUrlRegex.test(updatedDomainUrl)) {
            formik.setFieldError(
                "domainUrl",
                "Domain URL contains invalid characters. Only letters, numbers, periods, and hyphens are allowed."
            );
            return;
        }

        // Update the formik value with the full domain URL
        formik.setFieldValue("domainUrl", updatedDomainUrl);
        setIsEditing(false);
        // setIsButtonDisabled(false);
        setHasCustomDomain(true);
    };

    // useEffect(() => {
    //     if (formik.values.isVerified) return;
    //     if (!hasCustomDomain && formik.values.userName) {
    //         const generatedUrl = generateDefaultDomainUrl(
    //             formik.values.userName
    //         );
    //         if (formik.values.domainUrl !== generatedUrl) {
    //             formik.setFieldValue("domainUrl", generatedUrl);
    //         }
    //     }
    // }, [formik.values.userName]);

    useEffect(() => {
        if (
            !hasCustomDomain &&
            formik.values.userName &&
            (!organizationDataById || isTrialPortalCreation)
        ) {
            const generatedUrl = generateDefaultDomainUrl(
                formik.values.userName
            );

            if (formik.values.domainUrl !== generatedUrl) {
                formik.setFieldValue("domainUrl", generatedUrl);
            }
        }
    }, [
        formik.values.userName,
        organizationDataById,
        isTrialPortalCreation
    ]);

    // useEffect(() => {
    //     if (organizationDataById) {
    //         setIsEditing(false);
    //     }
    // }, [organizationDataById]);

    useEffect(() => {
        if (organizationDataById && !isTrialPortalCreation) {
            setIsEditing(false);
        }
    }, [organizationDataById, isTrialPortalCreation]);

    const verifyDomainURL = async () => {
        try {
            const paycentralDomain = formik?.values?.domainUrl?.trim()?.toLowerCase()?.replace(/^https?:\/\//, "");;
            const qbotDomainValue = qbotDomain?.values?.domainUrl?.trim()?.toLowerCase()?.replace(/^https?:\/\//, "");;

            if (
                qbotDomainValue &&
                paycentralDomain &&
                qbotDomainValue === paycentralDomain
            ) {
                formik.setFieldError(
                    "domainUrl",
                    "QBOT and PayCentral domains cannot be the same."
                );
                return;
            }

            const response = await verifyDomain({
                domainUrl: `${formik?.values?.domainUrl}`,
            });
            if (response.data.status === 200) {
                setSnackbar({
                    open: true,
                    message: response.data.message,
                    severity: "error",
                });
                setButtonColor("error");
                setIsVerified(false);
                setIsButtonDisabled(false);
            } else if (response.data.status === 202) {
                setButtonColor("success");
                setIsVerified(true);
                setIsButtonDisabled(true);
                formik.setFieldValue("isVerified", true);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response.data.message,
                severity: "error",
            });
            setIsButtonDisabled(false);
        }
    };

    useEffect(() => {
        if (formik.values.isVerified) {
            setButtonColor("success");
            setIsButtonDisabled(true);
        }
    }, [formik.values.isVerified]);

    useEffect(() => {
        if (!organizationDataById) return;

        const loadStates = async () => {

            // Business Country states
            if (organizationDataById.businessCountry) {
                await getBusinessStateDate(organizationDataById.businessCountry);
                formik.setFieldValue(
                    "businessState",
                    organizationDataById.businessState || ""
                );
            }

            // Organization Country states
            if (organizationDataById.country) {
                await getStateData(organizationDataById.country);
                formik.setFieldValue(
                    "state",
                    organizationDataById.state || ""
                );
            }
        };

        loadStates();
    }, [organizationDataById]);

    useEffect(() => {
        if (formik.values.businessCountry) {
            getBusinessStateDate(formik.values.businessCountry);
        }
    }, [formik.values.businessCountry]);

    useEffect(() => {

        if (!qbotDomain.values?.companyName || !formik.values.userName) return;

        const clean = (value) =>
            value?.trim().toLowerCase().replace(/[^a-zA-Z0-9]/g, "");

        const paycentralUserName = clean(formik.values.userName);
        const qbotCompanyName = clean(qbotDomain.values.companyName);

        if (paycentralUserName === qbotCompanyName) {
            formik.setFieldError(
                "userName",
                "QBOT company name and PayCentral user name cannot be the same."
            );
        }

    }, [formik.values.userName, qbotDomain?.values?.companyName]);

    return (
        <div className="org-step">
            <section className="org-step-section">
                <div className="org-step-section-head">
                    <h3>Organization identity</h3>
                    <p>User name, corporation name, and domain.</p>
                </div>
                <div className="org-step-grid">
                    <div className="org-step-field">
                        <label htmlFor="org-userName">User Name</label>
                        <TextField
                            id="org-userName"
                            fullWidth
                            size="small"
                            name="userName"
                            sx={stepperTextFieldSx}
                            value={formik.values.userName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.userName &&
                                Boolean(formik.errors.userName)
                            }
                            helperText={
                                formik.touched.userName && formik.errors.userName
                            }
                            disabled={Boolean(organizationDataById) && !isTrialPortalCreation}
                        />
                    </div>

                    <div className="org-step-field">
                        <label htmlFor="org-organizationName">Corporation Name</label>
                        <TextField
                            id="org-organizationName"
                            fullWidth
                            size="small"
                            name="organizationName"
                            sx={stepperTextFieldSx}
                            value={formik.values.organizationName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.organizationName && Boolean(formik.errors.organizationName)}
                            helperText={formik.touched.organizationName && formik.errors.organizationName}
                        />
                    </div>

                    {(formik.values.userName || formik.values.domainUrl || !organizationDataById || isTrialPortalCreation) && (
                        <div className="org-step-field is-full">
                            <label htmlFor="org-domainUrl">Domain</label>
                            <div className="org-step-domain">
                                <div className="org-step-domain-input">
                                    <TextField
                                        id="org-domainUrl"
                                        name="domainUrl"
                                        type="text"
                                        size="small"
                                        sx={stepperTextFieldSx}
                                        fullWidth
                                        value={formik.values.domainUrl}
                                        onChange={(e) => {
                                            if (isEditing) {
                                                formik.setFieldValue("domainUrl", e.target.value);
                                                formik.setFieldValue("isVerified", false);
                                                setIsButtonDisabled(false);
                                                setButtonColor("");
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.domainUrl && Boolean(formik.errors.domainUrl)}
                                        helperText={formik.touched.domainUrl && formik.errors.domainUrl}
                                        disabled={!isEditing || (Boolean(organizationDataById) && !isTrialPortalCreation)}
                                        placeholder={isEditing ? "Enter subdomain" : ""}
                                    />
                                </div>

                                <div className="org-step-domain-actions">
                                    {isEditing ? (
                                        <Tooltip title="Save">
                                            <IconButton
                                                className="text-success border border-success is-success"
                                                type="button"
                                                onClick={handleSaveDomainUrl}
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <>
                                            {(!isEditMode || isTrialPortalCreation) && (
                                                <Tooltip title="Edit">
                                                    <span
                                                        style={{
                                                            cursor: !formik.values.domainUrl ? "not-allowed" : "pointer",
                                                        }}
                                                    >
                                                        <IconButton
                                                            className="text-primary border border-primary"
                                                            type="button"
                                                            onClick={handleEditClick}
                                                            disabled={!formik.values.domainUrl}
                                                            sx={{ "&.Mui-disabled": { opacity: 0.5 } }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            {!isEditing && (
                                                <Tooltip title={formik.values.isVerified ? "Verified" : "Verify Domain"}>
                                                    <span
                                                        style={{
                                                            cursor: (!isEditing &&
                                                                Boolean(organizationDataById) &&
                                                                !isTrialPortalCreation) || formik.values.isVerified
                                                                ? "not-allowed"
                                                                : "pointer",
                                                        }}
                                                    >
                                                        <IconButton
                                                            className={
                                                                buttonColor === "success" || formik.values.isVerified
                                                                    ? "text-success border border-success is-success"
                                                                    : buttonColor === "error"
                                                                        ? "text-danger border border-danger is-danger"
                                                                        : "text-primary border border-primary"
                                                            }
                                                            onClick={verifyDomainURL}
                                                            disabled={(!isEditing &&
                                                                Boolean(organizationDataById) &&
                                                                !isTrialPortalCreation) || formik.values.isVerified}
                                                            sx={{ "&.Mui-disabled": { opacity: 0.5 } }}
                                                        >
                                                            <VerifiedIcon
                                                                fontSize="small"
                                                                className={
                                                                    buttonColor === "success" || formik.values.isVerified
                                                                        ? "text-success"
                                                                        : buttonColor === "error"
                                                                            ? "text-danger"
                                                                            : "text-primary"
                                                                }
                                                            />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {formik.errors.isVerified && (
                                <span className="org-step-error">
                                    {formik.errors.isVerified}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <section className="org-step-section">
                <div className="org-step-section-head">
                    <h3>Business details</h3>
                    <p>Company profile, location, website, and tax information.</p>
                </div>
                <div className="org-step-grid">
                    <div className="org-step-field">
                        <label htmlFor="org-businessName">Business Name</label>
                        <TextField
                            id="org-businessName"
                            fullWidth
                            size="small"
                            name="businessName"
                            sx={stepperTextFieldSx}
                            value={formik.values.businessName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.businessName && Boolean(formik.errors.businessName)}
                            helperText={formik.touched.businessName && formik.errors.businessName}
                        />
                    </div>

                    <div className="org-step-field">
                        <label>Business Type</label>
                        <Autocomplete
                            fullWidth
                            options={businessType || []}
                            getOptionLabel={(option) => option?.name || ""}
                            value={businessType?.find((opt) => opt.name === formik.values?.businessType) || null}
                            onChange={(event, value) => formik.setFieldValue("businessType", value?.name)}
                            onBlur={formik.handleBlur}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={stepperAutocompleteInputSx}
                                    error={formik.touched.businessType && Boolean(formik.errors.businessType)}
                                    helperText={formik.touched.businessType && formik.errors.businessType}
                                />
                            )}
                        />
                    </div>

                    <div className="org-step-field is-full">
                        <label htmlFor="org-businessDescription">Business Description</label>
                        <TextField
                            id="org-businessDescription"
                            multiline
                            rows={2}
                            fullWidth
                            sx={stepperTextareaSx}
                            name="businessDescription"
                            value={formik.values.businessDescription}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.businessDescription && Boolean(formik.errors.businessDescription)}
                            helperText={formik.touched.businessDescription && formik.errors.businessDescription}
                        />
                    </div>

                    <div className="org-step-field">
                        <label>Business Country</label>
                        <Autocomplete
                            fullWidth
                            options={countryData || []}
                            getOptionLabel={(option) => option?.name || ""}
                            value={countryData?.find((opt) => opt?.name === formik.values.businessCountry) || null}
                            onChange={(event, value) => {
                                formik.setFieldValue("businessCountry", value?.name);
                                formik.setFieldValue("businessState", "");
                                getBusinessStateDate(value?.name);
                            }}
                            onBlur={formik.handleBlur}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={stepperAutocompleteInputSx}
                                    error={formik.touched.businessCountry && Boolean(formik.errors.businessCountry)}
                                    helperText={formik.touched.businessCountry && formik.errors.businessCountry}
                                />
                            )}
                        />
                    </div>

                    <div className="org-step-field">
                        <label>Business State</label>
                        <Autocomplete
                            fullWidth
                            options={businessStateData || []}
                            getOptionLabel={(option) => option?.name || ""}
                            value={
                                businessStateData?.length
                                    ? businessStateData.find(
                                        (opt) => opt.name === formik.values.businessState
                                    ) || null
                                    : null
                            }
                            onChange={(event, value) => {
                                formik.setFieldValue("businessState", value?.name || "");
                                formik.setFieldTouched("businessState", true);
                            }}
                            onBlur={() => formik.setFieldTouched("businessState", true)}
                            disabled={!(businessStateData?.length)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={stepperAutocompleteInputSx}
                                    error={formik.touched.businessState && Boolean(formik.errors.businessState)}
                                    helperText={formik.touched.businessState && formik.errors.businessState}
                                />
                            )}
                        />
                    </div>

                    <div className="org-step-field">
                        <label htmlFor="org-website">Website URL</label>
                        <TextField
                            id="org-website"
                            fullWidth
                            size="small"
                            name="website"
                            sx={stepperTextFieldSx}
                            value={formik.values.website}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.website && Boolean(formik.errors.website)}
                            helperText={formik.touched.website && formik.errors.website}
                        />
                    </div>

                    <div className="org-step-field">
                        <label htmlFor="org-taxId">Tax ID / Business Registration Number</label>
                        <TextField
                            id="org-taxId"
                            fullWidth
                            size="small"
                            name="taxId"
                            sx={stepperTextFieldSx}
                            value={formik.values.taxId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.taxId && Boolean(formik.errors.taxId)}
                            helperText={formik.touched.taxId && formik.errors.taxId}
                        />
                    </div>
                </div>
            </section>

            <section className="org-step-section">
                <div className="org-step-section-head">
                    <h3>Localization</h3>
                    <p>Date format, time zone, time format, and IP address.</p>
                </div>
                <div className="org-step-grid">
                    <div className="org-step-field">
                        <label>Date Format</label>
                        <Autocomplete
                            options={dateFormatOptions || []}
                            getOptionLabel={(option) => option.label}
                            value={dateFormatOptions?.find((opt) => opt.value === formik.values.dateFormat) || null}
                            onChange={handleDateFormatChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    fullWidth
                                    sx={stepperAutocompleteInputSx}
                                    error={formik.touched.dateFormat && Boolean(formik.errors.dateFormat)}
                                    helperText={formik.touched.dateFormat && formik.errors.dateFormat}
                                />
                            )}
                        />
                    </div>

                    <div className="org-step-field">
                        <label>Time Zone</label>
                        <Autocomplete
                            options={Array.isArray(timeZone) ? timeZone : []}
                            getOptionLabel={(option) => option?.timeZone || ""}
                            value={Array.isArray(timeZone) ? timeZone.find((opt) => opt.timeZone === formik.values.timeZone) || null : null}
                            onChange={handleTimeZoneChange}
                            renderOption={(props, option, { index }) => (
                                <li {...props} key={`${option.timeZone}-${index}`}>
                                    {option.timeZone}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={stepperAutocompleteInputSx}
                                    fullWidth
                                    error={formik.touched.timeZone && Boolean(formik.errors.timeZone)}
                                    helperText={formik.touched.timeZone && formik.errors.timeZone}
                                />
                            )}
                        />
                    </div>

                    <div className="org-step-field">
                        <label>Time Format</label>
                        <Autocomplete
                            options={timeFormatOptions || []}
                            getOptionLabel={(option) => option.label}
                            value={timeFormatOptions?.find((opt) => opt.value === formik.values.timeFormat) || null}
                            onChange={handleTimeFormatChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={stepperAutocompleteInputSx}
                                    fullWidth
                                    error={formik.touched.timeFormat && Boolean(formik.errors.timeFormat)}
                                    helperText={formik.touched.timeFormat && formik.errors.timeFormat}
                                />
                            )}
                        />
                    </div>

                    {(!organizationDataById || isTrialPortalCreation) && (
                        <div className="org-step-field">
                            <label htmlFor="org-ipAddress">Ip Address</label>
                            <TextField
                                id="org-ipAddress"
                                fullWidth
                                size="small"
                                name="ipAddress"
                                sx={stepperTextFieldSx}
                                value={formik.values.ipAddress}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.ipAddress && Boolean(formik.errors.ipAddress)}
                                helperText={formik.touched.ipAddress && formik.errors.ipAddress}
                                disabled={Boolean(organizationDataById) && !isTrialPortalCreation}
                            />
                        </div>
                    )}
                </div>
            </section>

            <section className="org-step-section">
                <div className="org-step-section-head">
                    <h3>Status & branding</h3>
                    <p>Activation, invoicing preference, and organization logo.</p>
                </div>
                <div className="org-step-grid">
                    <div className="org-step-field is-full">
                        <div className="org-step-toggles">
                            <div
                                className={`org-step-toggle${
                                    formik.values.isActive ? " is-on" : ""
                                }`}
                            >
                                <div className="org-step-toggle-copy">
                                    <strong>Active</strong>
                                    <span>
                                        {formik.values.isActive
                                            ? "Organization is available"
                                            : "Organization is inactive"}
                                    </span>
                                </div>
                                <CustomSwitch
                                    name="isActive"
                                    checked={formik.values.isActive}
                                    onChange={(event) =>
                                        formik.setFieldValue("isActive", event.target.checked)
                                    }
                                />
                            </div>

                            <div
                                className={`org-step-toggle${
                                    formik.values.isAutoInvoice ? " is-on" : ""
                                }`}
                            >
                                <div className="org-step-toggle-copy">
                                    <strong>Enable Auto Invoice</strong>
                                    <span>
                                        {formik.values.isAutoInvoice
                                            ? "Invoices generate automatically"
                                            : "Manual invoice generation"}
                                    </span>
                                </div>
                                <CustomSwitch
                                    name="isAutoInvoice"
                                    checked={formik.values.isAutoInvoice}
                                    onChange={(event) =>
                                        formik.setFieldValue("isAutoInvoice", event.target.checked)
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="org-step-field is-full">
                        <label>Image</label>
                        <label className="org-step-upload">
                            {formik.values.imagePreview ? (
                                <div className="org-step-upload-preview">
                                    <Avatar
                                        src={formik.values.imagePreview}
                                        sx={{
                                            width: 100,
                                            height: 100,
                                            margin: "0 auto",
                                            border: "2px solid #22419e",
                                        }}
                                    />
                                    <Button
                                        className="org-step-upload-remove"
                                        size="small"
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            formik.setFieldValue("organizationLogo", null);
                                            formik.setFieldValue("imagePreview", null);
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <span className="org-step-upload-icon" aria-hidden>
                                        <CloudUploadIcon />
                                    </span>
                                    <div className="org-step-upload-copy">
                                        <strong>Click to upload or drag and drop</strong>
                                        <span>PNG, JPG, GIF or WebP (Max 5MB)</span>
                                    </div>
                                </>
                            )}

                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                onBlur={formik.handleBlur}
                            />
                        </label>

                        {formik.touched.organizationLogo &&
                            formik.errors.organizationLogo && (
                                <span className="org-step-error">
                                    {formik.errors.organizationLogo}
                                </span>
                            )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OrganizationInfoStep;
